<template>
  <div>
    <!-- Header -->
    <div class="mb-6">
      <div class="flex items-center justify-between mb-2">
        <h2 class="text-xl font-semibold text-gray-900">Procesando imágenes...</h2>
        <div v-if="statusLabel === 'PROCESANDO'" class="flex items-center gap-2 text-sm text-gray-600">
          <div class="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
          <span>En progreso</span>
        </div>
      </div>
      <p class="text-sm text-gray-500">
        Estamos analizando las imágenes y asignándolas a los residentes correspondientes. Este proceso puede tomar unos
        minutos.
      </p>
    </div>

    <!-- Progreso visual mejorado -->
    <div class="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 mb-6">
      <!-- Stats principales -->
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <div class="bg-white rounded-lg p-3 shadow-sm">
          <div class="text-xs text-gray-500 mb-1">Total</div>
          <div class="text-2xl font-bold text-gray-900">{{ total || '—' }}</div>
        </div>
        <div class="bg-white rounded-lg p-3 shadow-sm">
          <div class="text-xs text-gray-500 mb-1">Procesadas</div>
          <div class="text-2xl font-bold text-blue-600">{{ processed }}</div>
        </div>
        <div class="bg-white rounded-lg p-3 shadow-sm">
          <div class="text-xs text-gray-500 mb-1">Restantes</div>
          <div class="text-2xl font-bold text-gray-600">{{ remaining }}</div>
        </div>
        <div class="bg-white rounded-lg p-3 shadow-sm">
          <div class="text-xs text-gray-500 mb-1">Progreso</div>
          <div class="text-2xl font-bold text-green-600">{{ progressPct }}%</div>
        </div>
      </div>

      <!-- Barra de progreso -->
      <div class="relative">
        <div class="w-full bg-blue-100 rounded-full h-3 overflow-hidden" role="progressbar" :aria-valuemin="0"
          :aria-valuemax="total" :aria-valuenow="processed">
          <div
            class="bg-gradient-to-r from-blue-500 to-indigo-600 h-3 rounded-full transition-all duration-500 ease-out relative"
            :style="{ width: progressPct + '%' }">
            <div class="absolute inset-0 bg-white opacity-20 animate-pulse"></div>
          </div>
        </div>
        <div class="flex justify-between text-xs text-gray-600 mt-2">
          <span>{{ processed }} / {{ total || 0 }} imágenes</span>
          <span v-if="estimatedTimeRemaining">{{ estimatedTimeRemaining }}</span>
        </div>
      </div>

      <!-- Imagen actual -->
      <div v-if="currentImage" class="mt-4 bg-white rounded-lg p-3 border border-blue-200">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
            <svg class="w-5 h-5 text-blue-600 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
              </path>
            </svg>
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-xs text-gray-500 mb-0.5">Procesando ahora:</p>
            <p class="text-sm font-medium text-gray-900 truncate" :title="currentImage">{{ currentImage }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Actividades recientes (en lugar de logs técnicos) -->
    <div class="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div class="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
        <h3 class="text-sm font-semibold text-gray-800 flex items-center gap-2">
          <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
          Actividades recientes
        </h3>
        <div class="flex items-center gap-2">
          <span v-if="retrying" class="text-xs text-amber-600 italic flex items-center gap-1">
            <svg class="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
              </path>
            </svg>
            Reconectando...
          </span>
          <button class="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 transition-colors"
            @click="clearLogs">
            Limpiar
          </button>
        </div>
      </div>

      <div ref="logBox" class="h-48 overflow-y-auto px-4 py-3 bg-gray-50" aria-live="polite" aria-atomic="false">
        <template v-if="userFriendlyLogs.length">
          <div v-for="(log, idx) in userFriendlyLogs" :key="idx"
            class="mb-2 pb-2 border-b border-gray-200 last:border-0">
            <div class="flex items-start gap-2">
              <!-- Icono según tipo -->
              <div class="flex-shrink-0 mt-0.5">
                <svg v-if="log.type === 'success'" class="w-4 h-4 text-green-600" fill="none" stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
                <svg v-else-if="log.type === 'error'" class="w-4 h-4 text-red-600" fill="none" stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <svg v-else-if="log.type === 'warning'" class="w-4 h-4 text-amber-600" fill="none" stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <svg v-else class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>

              <div class="flex-1 min-w-0">
                <p class="text-sm text-gray-900">{{ log.message }}</p>
                <p class="text-xs text-gray-500 mt-0.5">{{ log.time }}</p>
              </div>
            </div>
          </div>
        </template>
        <div v-else class="text-center py-8 text-gray-400">
          <svg class="w-12 h-12 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p class="text-sm">Esperando actividades...</p>
        </div>
      </div>
    </div>

    <!-- Estado/Errores -->
    <div class="mt-6">
      <div v-if="errorMsg" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
        <div class="flex items-start gap-3">
          <svg class="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div class="flex-1">
            <p class="font-medium text-red-900">Error de procesamiento</p>
            <p class="text-sm text-red-700 mt-1">{{ errorMsg }}</p>
          </div>
        </div>
      </div>

      <!-- Acciones -->
      <div class="flex items-center justify-end gap-3">
        <button v-if="failed" type="button"
          class="px-6 py-2 border-2 border-red-300 text-red-700 rounded-lg hover:bg-red-50 font-medium transition-colors flex items-center gap-2"
          @click="manualRetry">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Reintentar procesamiento
        </button>

        <button v-if="doneOk" type="button"
          class="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors shadow-lg hover:shadow-xl flex items-center gap-2"
          @click="$emit('done', { status: 'DONE', summary })">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
          Continuar a revisión
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onBeforeUnmount, watch } from 'vue'
import { openIngestSSE } from '@/store/facialIngest'

export default {
  name: 'IngestProgress',
  props: {
    token: { type: String, required: false },
    baseUrl: { type: String, default: '/api/residents/facial-recognition' },
    jobId: { type: [String, Number], required: true },
    authInQuery: { type: Boolean, default: true },
    retryMax: { type: Number, default: 6 },
    retryBaseMs: { type: Number, default: 800 },
    retryMaxDelayMs: { type: Number, default: 8000 }
  },
  setup(props, { emit }) {
    const processed = ref(0)
    const total = ref(0)
    const currentImage = ref('')
    const status = ref('RUNNING')
    const summary = reactive({})
    const errorMsg = ref('')
    const failed = ref(false)
    const doneOk = ref(false)

    const rawLogs = ref([])
    const retryCount = ref(0)
    const retrying = ref(false)
    const startTime = ref(Date.now())

    const logBox = ref(null)

    let sseConnection = null
    let closed = false
    let retryTimeout = null

    const progressPct = computed(() => {
      if (!total.value) return 0
      const pct = (processed.value / total.value) * 100
      return Math.max(0, Math.min(100, Math.round(pct)))
    })

    const remaining = computed(() => {
      return Math.max(0, (total.value || 0) - processed.value)
    })

    const estimatedTimeRemaining = computed(() => {
      if (!processed.value || !total.value || processed.value === 0) return null

      const elapsed = Date.now() - startTime.value
      const avgTimePerImage = elapsed / processed.value
      const remainingMs = avgTimePerImage * remaining.value

      if (remainingMs < 60000) {
        return `~${Math.ceil(remainingMs / 1000)}s restantes`
      } else {
        return `~${Math.ceil(remainingMs / 60000)}m restantes`
      }
    })

    const statusLabel = computed(() => {
      if (failed.value) return 'FALLÓ'
      if (doneOk.value) return 'COMPLETADO'
      return 'PROCESANDO'
    })

    // Logs user-friendly (filtrados y formateados)
    const userFriendlyLogs = computed(() => {
      return rawLogs.value
        .filter(log => {
          // Filtrar logs técnicos no relevantes para usuario
          const msg = log.message.toLowerCase()
          return !msg.includes('uuid') &&
            !msg.includes('jobid') &&
            !msg.includes('token') &&
            !msg.includes('connecting') &&
            !msg.includes('sse')
        })
        .map(log => {
          let type = 'info'
          let cleanMessage = log.message

          // Detectar tipo de log
          if (cleanMessage.includes('completado') || cleanMessage.includes('éxito') || cleanMessage.includes('exitosa')) {
            type = 'success'
          } else if (cleanMessage.includes('error') || cleanMessage.includes('falló') || cleanMessage.includes('fallo')) {
            type = 'error'
          } else if (cleanMessage.includes('advertencia') || cleanMessage.includes('warning')) {
            type = 'warning'
          }

          // Limpiar mensaje de datos técnicos
          cleanMessage = cleanMessage
            .replace(/\[.*?\]/g, '') // Remover [tags]
            .replace(/uuid:\s*[a-f0-9-]+/gi, '') // Remover UUIDs
            .replace(/id:\s*\d+/gi, '') // Remover IDs numéricos
            .trim()

          return {
            ...log,
            message: cleanMessage,
            type
          }
        })
        .slice(-10) // Solo últimos 10 logs
    })

    function pushLog(message) {
      rawLogs.value.push({ time: new Date().toLocaleTimeString(), message })
      requestAnimationFrame(() => {
        if (logBox.value) {
          logBox.value.scrollTop = logBox.value.scrollHeight
        }
      })
    }

    function processEvent(data) {
      let type = data.type

      if (!type) {
        if ('succeeded' in data || 'failures' in data) {
          type = 'UploadZipReport'
        } else if ('percent' in data || 'processed' in data || 'lastFile' in data) {
          type = 'ProgressUpdate'
        } else {
          type = 'LOG'
        }
      }

      const stateOrStatus = data.status || data.state

      if (type === 'ProgressUpdate') {
        if (data.processed != null) processed.value = data.processed
        if (data.total != null) total.value = data.total

        currentImage.value = data.currentImage || data.lastFile || currentImage.value

        if (data.message) pushLog(data.message)
        if (stateOrStatus) status.value = stateOrStatus

      } else if (type === 'UploadZipReport') {
        const sum = data.summary || {
          total: data.total,
          processed: data.processed,
          succeeded: data.succeeded,
          failed: data.failed,
          failures: data.failures
        }

        Object.assign(summary, sum)

        if (sum.total != null) total.value = sum.total
        if (sum.processed != null) processed.value = sum.processed

        if (data.message) pushLog(data.message)

        const finalStatus = stateOrStatus || 'DONE'

        if (finalStatus === 'DONE') {
          status.value = 'DONE'
          doneOk.value = true
          pushLog('Procesamiento completado exitosamente')
          closeStream()
          emit('done', { status: 'DONE', summary })
        } else if (finalStatus === 'FAILED') {
          status.value = 'FAILED'
          failed.value = true
          const msg = data.error || data.message || 'El procesamiento falló.'
          errorMsg.value = msg
          pushLog(`Error: ${msg}`)
          closeStream()
          emit('failed', { error: msg })
        }

      } else if (type === 'LOG') {
        if (data.message) pushLog(data.message)
      } else {
        pushLog(`Evento: ${JSON.stringify(data)}`)
      }
    }

    function openStream() {
      cleanupStream()
      errorMsg.value = ''
      failed.value = false
      retrying.value = false

      try {
        pushLog('Iniciando procesamiento...')

        sseConnection = openIngestSSE({
          jobId: props.jobId,

          onOpen: () => {
            pushLog('Conexión establecida')
            retryCount.value = 0
            retrying.value = false
          },

          onEvent: (data) => {
            processEvent(data)
          },

          onError: () => {
            if (doneOk.value || failed.value || closed) return
            pushLog('Error en la conexión')
            handleReconnect()
          }
        })

      } catch (e) {
        handleStreamError('No se pudo iniciar el procesamiento: ' + e.message)
      }
    }

    function handleReconnect() {
      if (doneOk.value || failed.value || closed) return

      if (retryCount.value >= props.retryMax) {
        failed.value = true
        const msg = 'No se pudo mantener la conexión con el servidor.'
        errorMsg.value = msg
        pushLog(msg)
        closeStream()
        emit('failed', { error: msg })
        return
      }

      retryCount.value += 1
      retrying.value = true

      const base = Math.min(
        props.retryBaseMs * Math.pow(2, retryCount.value - 1),
        props.retryMaxDelayMs
      )
      const jitter = Math.floor(Math.random() * (base * 0.25))
      const delay = base + jitter

      pushLog(`Reconectando (intento ${retryCount.value}/${props.retryMax})...`)
      retryTimeout = setTimeout(() => {
        if (!closed) openStream()
      }, delay)
    }

    function handleStreamError(msg) {
      errorMsg.value = msg
      pushLog(msg)
      emit('error', msg)
      handleReconnect()
    }

    function manualRetry() {
      failed.value = false
      errorMsg.value = ''
      retryCount.value = 0
      startTime.value = Date.now()
      openStream()
    }

    function closeStream() {
      closed = true
      if (retryTimeout) {
        clearTimeout(retryTimeout)
        retryTimeout = null
      }
      if (sseConnection) {
        sseConnection.close()
        sseConnection = null
      }
    }

    function cleanupStream() {
      if (retryTimeout) {
        clearTimeout(retryTimeout)
        retryTimeout = null
      }
      if (sseConnection) {
        sseConnection.close()
        sseConnection = null
      }
      closed = false
    }

    function clearLogs() {
      rawLogs.value = []
    }

    watch(
      () => props.jobId,
      (next) => {
        if (!next) return
        processed.value = 0
        total.value = 0
        currentImage.value = ''
        status.value = 'RUNNING'
        doneOk.value = false
        failed.value = false
        errorMsg.value = ''
        startTime.value = Date.now()
        clearLogs()
        openStream()
      },
      { immediate: true }
    )

    onBeforeUnmount(() => {
      closeStream()
    })

    return {
      processed,
      total,
      remaining,
      currentImage,
      progressPct,
      estimatedTimeRemaining,
      statusLabel,
      failed,
      doneOk,
      errorMsg,
      userFriendlyLogs,
      retryCount,
      retrying,
      summary,
      logBox,
      manualRetry,
      clearLogs
    }
  }
}
</script>

<style scoped>
/* Scrollbar personalizado */
[ref="logBox"]::-webkit-scrollbar {
  width: 6px;
}

[ref="logBox"]::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

[ref="logBox"]::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

[ref="logBox"]::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>